import { TextField } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import {
    clientAutocomplete,
    chainAutocomplete,
    onlyVendorAutocomplete,
    activityAutocomplete
} from '../autocomplete';
import AutocompleteSelect from '../components/atom/AutocompleteSelect';
import HeightTextbox from '../components/atom/HeightTextbox';
import NumberTextbox from '../components/atom/NumberTextbox';
import PriceRuleCheckbox from '../components/atom/PriceRuleCheckbox';
import PriceRuleDateRange from '../components/atom/PriceRuleDateRange';
import PriceRuleSelect from '../components/atom/PriceRuleSelect';
import PriceRuleAutoComplete from '../components/atom/PriceRuleAutoComplete';
import PriceRuleTextbox from '../components/atom/PriceRuleTextbox';
import PriceRuleTimeRange from '../components/atom/PriceRuleTimeRange';
import { useIslands } from './useIslands';

const CONDITION_TYPES = [
    {
        key: 'client',
        name: 'Client is in',
        dataType: 'client',
        defaultValue: [],
        renderComponent: AutocompleteSelect,
        otherProps: {
            multiple: true,
            renderInput: params => (
                <TextField {...params} label="Client name" style={{ minWidth: '260px' }} />
            ),
            initCall: clientAutocomplete.init,
            autocompleteCall: clientAutocomplete.search
        }
    },
    {
        key: 'vendor',
        name: 'Vendor is in',
        dataType: 'vendor',
        defaultValue: [],
        renderComponent: AutocompleteSelect,
        otherProps: {
            multiple: true,
            renderInput: params => (
                <TextField {...params} label="Vendor name" style={{ minWidth: '260px' }} />
            ),
            initCall: onlyVendorAutocomplete.init,
            autocompleteCall: onlyVendorAutocomplete.search
        }
    },
    {
        key: 'activity',
        name: 'Activity is in',
        dataType: 'activity',
        defaultValue: [],
        renderComponent: AutocompleteSelect,
        otherProps: {
            multiple: true,
            renderInput: params => (
                <TextField {...params} label="Activity name" style={{ minWidth: '260px' }} />
            ),
            initCall: activityAutocomplete.init,
            autocompleteCall: activityAutocomplete.search
        }
    },
    {
        key: 'tag',
        name: 'Client has tag',
        dataType: 'tag',
        defaultValue: [],
        renderComponent: props => <PriceRuleAutoComplete {...props} type="tag" />
    },
    {
        key: 'island',
        name: 'Island is in',
        dataType: 'island',
        defaultValue: [],
        renderComponent: PriceRuleSelect,
        otherProps: {
            options: [],
            multiple: true
        }
    },
    {
        key: 'dayOfWeek',
        name: 'Day of week is in',
        dataType: 'dayOfWeek',
        defaultValue: [],
        renderComponent: PriceRuleSelect,
        otherProps: {
            options: [
                { id: 0, name: 'Sunday' },
                { id: 1, name: 'Monday' },
                { id: 2, name: 'Tuesday' },
                { id: 3, name: 'Wednesday' },
                { id: 4, name: 'Thursday' },
                { id: 5, name: 'Friday' },
                { id: 6, name: 'Saturday' }
            ],
            multiple: true
        }
    },
    {
        key: 'date',
        name: 'Seasonal date (month + day)',
        dataType: 'date',
        defaultValue: ['01-01', '12-31'],
        renderComponent: PriceRuleDateRange,
        otherProps: {
            format: 'MM-DD',
            minDate: '01-01',
            maxDate: '12-31'
        }
    },
    {
        key: 'daterange',
        name: 'Date is between',
        dataType: 'date',
        defaultValue: ['2022-01-01', '2022-12-31'],
        renderComponent: PriceRuleDateRange,
        otherProps: {
            format: 'YYYY-MM-DD',
            minDate: '2021-01-01',
            maxDate: '2030-12-31'
        }
    },
    {
        key: 'time',
        name: 'Time is between',
        dataType: 'time',
        defaultValue: ['00:00', '23:59'],
        renderComponent: PriceRuleTimeRange,
        otherProps: {
            format: 'HH:mm'
        }
    },
    {
        key: 'inChain',
        name: 'Client is in chain',
        dataType: 'inChain',
        defaultValue: [],
        renderComponent: AutocompleteSelect,
        otherProps: {
            multiple: true,
            renderInput: params => (
                <TextField {...params} label="Chain name" style={{ minWidth: '260px' }} />
            ),
            initCall: chainAutocomplete.init,
            autocompleteCall: chainAutocomplete.search
        }
    },
    {
        key: 'notInChain',
        name: 'Client is not in chain',
        dataType: 'notInChain',
        defaultValue: [],
        renderComponent: AutocompleteSelect,
        otherProps: {
            multiple: true,
            renderInput: params => (
                <TextField {...params} label="Chain name" style={{ minWidth: '260px' }} />
            ),
            initCall: chainAutocomplete.init,
            autocompleteCall: chainAutocomplete.search
        }
    }
];

function bookingFieldsToConditionTypes({ id, type, name, options }) {
    switch (type) {
        case 'boolean':
            return [
                {
                    key: `${id}:is`,
                    name,
                    defaultValue: true,
                    dataType: 'boolean',
                    renderComponent: PriceRuleCheckbox,
                    otherProps: {
                        label: name
                    }
                }
            ];
        case 'number':
            return [
                {
                    key: `${id}:min`,
                    name: `${name} is larger than`,
                    dataType: 'number',
                    defaultValue: 0,
                    renderComponent: NumberTextbox,
                    otherProps: {
                        variant: 'standard'
                    }
                },
                {
                    key: `${id}:max`,
                    name: `${name} is smaller than`,
                    dataType: 'number',
                    defaultValue: 0,
                    renderComponent: NumberTextbox,
                    otherProps: {
                        variant: 'standard'
                    }
                }
            ];
        case 'weight':
            return [
                {
                    key: `${id}:min`,
                    name: `${name} is more than (lbs)`,
                    dataType: 'number',
                    defaultValue: 0,
                    renderComponent: NumberTextbox,
                    otherProps: {
                        variant: 'standard'
                    }
                },
                {
                    key: `${id}:max`,
                    name: `${name} is less than (lbs)`,
                    dataType: 'number',
                    defaultValue: 0,
                    renderComponent: NumberTextbox,
                    otherProps: {
                        variant: 'standard'
                    }
                }
            ];

        case 'height':
            return [
                {
                    key: `${id}:min`,
                    name: `${name} is taller than (inches)`,
                    dataType: 'number',
                    defaultValue: 0,
                    renderComponent: HeightTextbox,
                    otherProps: {
                        variant: 'standard',
                        disabledDropdown: true
                    }
                },
                {
                    key: `${id}:max`,
                    name: `${name} is shorter than (inches)`,
                    dataType: 'number',
                    defaultValue: 0,
                    renderComponent: HeightTextbox,
                    otherProps: {
                        variant: 'standard',
                        disabledDropdown: true
                    }
                }
            ];
        case 'string':
            return [
                {
                    key: `${id}:is`,
                    name: `${name} is`,
                    defaultValue: '',
                    dataType: 'string',
                    renderComponent: PriceRuleTextbox,
                    otherProps: {
                        variant: 'standard'
                    }
                },
                {
                    key: `${id}:contains`,
                    name: `${name} contains`,
                    defaultValue: '',
                    dataType: 'string',
                    renderComponent: PriceRuleTextbox,
                    otherProps: {
                        variant: 'standard'
                    }
                }
            ];
        case 'select':
            // Handle both string and array formats for options
            const parsedOptions = (() => {
                if (!options) return [];
                if (Array.isArray(options)) {
                    return options.map((opt, idx) => ({
                        id: idx,
                        name: typeof opt === 'string' ? opt.trim() : opt.label
                    }));
                }
                return options.split(',').map((optionName, optionId) => ({
                    id: optionId,
                    name: optionName.trim()
                }));
            })();
            return [
                {
                    key: `${id}:in`,
                    name: `${name} is in`,
                    dataType: 'select',
                    defaultValue: [],
                    renderComponent: PriceRuleSelect,
                    otherProps: {
                        options: parsedOptions,
                        multiple: true
                    }
                }
            ];
        default:
            return [];
    }
}

export function getAllConditionTypes(islands, booking_fields) {
    let allCondition = CONDITION_TYPES;

    // replace island selector
    allCondition = allCondition.map(type => {
        // override island selector
        if (type.key === 'island') {
            return {
                ...type,
                otherProps: {
                    ...type.otherProps,
                    options: islands.map(({ id, name }) => ({
                        id,
                        name
                    }))
                }
            };
        }
        return type;
    });

    allCondition = [
        ...allCondition,
        ...booking_fields
            .map(bookingFieldsToConditionTypes)
            .reduce((sum, item) => [...sum, ...item], [])
    ];
    return allCondition;
}

export function usePriceruleConditions(booking_fields) {
    const [islands] = useIslands();
    const [ticketConditionTypes, setTicketConditionTypes] = useState([]);

    useEffect(() => {
        const allCondition = getAllConditionTypes(islands, booking_fields);
        setTicketConditionTypes(allCondition);
    }, [setTicketConditionTypes, islands, booking_fields]);

    return [ticketConditionTypes, booking_fields];
}
